import { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Card, Form, Table, Spinner, Button } from "react-bootstrap";
import {
  BiRefresh,
  BiPhoneCall,
  BiCheckCircle,
  BiXCircle,
  BiTime,
  BiTrendingUp,
  BiDollar,
  BiMap,
  BiArrowBack,
} from "react-icons/bi";
import { getOrderOperationsInsights } from "../api/getapi";
import { formatNumber } from "../Utilities/numbers";
import { ECHART_COLORS } from "../Utilities/echartsTheme";
import StatCard from "../Components/DashboardCards/StatCard/StatCard";
import EChartsGauge from "../Components/Charts/EChartsGauge";
import EChartsDoughnut from "../Components/Charts/EChartsDoughnut";
import EChartsVerticalBar from "../Components/Charts/EChartsVerticalBar";
import EChartsHorizontalBar from "../Components/Charts/EChartsHorizontalBar";

const spinKeyframes = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 0.8s linear infinite; display: inline-block; }`;

const timePeriods = [
  { value: "all-time", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this-week", label: "This Week" },
  { value: "this-month", label: "This Month" },
  { value: "this-year", label: "This Year" },
  { value: "last-year", label: "Last Year" },
];

const OrderOperationsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState("all-time");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOrderOperationsInsights({ timePeriod });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [timePeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const verificationRate = useMemo(() => {
    if (!data?.verification?.summary?.total) return 0;
    return Math.round((data.verification.summary.verified / data.verification.summary.total) * 100);
  }, [data]);

  const verificationDoughnutData = useMemo(() => {
    if (!data?.verification?.summary) return {};
    const s = data.verification.summary;
    return {
      Verified: s.verified,
      Failed: s.failed,
      Rejected: s.rejected,
      Escalated: s.escalated,
    };
  }, [data]);

  const trackingDoughnutData = useMemo(() => {
    if (!data?.tracking?.summary) return {};
    return data.tracking.summary.reduce((acc, item) => {
      acc[item.status] = item.count;
      return acc;
    }, {});
  }, [data]);

  const paymentMethodDoughnutData = useMemo(() => {
    if (!data?.payments?.byMethod) return {};
    return data.payments.byMethod.reduce((acc, item) => {
      acc[item.trx_method] = item.count;
      return acc;
    }, {});
  }, [data]);

  const verifyMonthlyBarData = useMemo(() => {
    if (!data?.verification?.monthlyTrend) return {};
    return [...data.verification.monthlyTrend]
      .reverse()
      .reduce((acc, m) => {
        acc[m.month] = Number(m.total) || 0;
        return acc;
      }, {});
  }, [data]);

  const deliveryCityBarData = useMemo(() => {
    if (!data?.deliveryCities) return [];
    return data.deliveryCities.map((c) => ({ label: c.city, value: c.count }));
  }, [data]);

  return (
    <Container fluid className="px-xl-5">
      <style>{spinKeyframes}</style>
      <Row className="py-2 align-items-center">
        <Col md="6">
          <h5 className="fw-bold">Order Operations Dashboard</h5>
          <p className="text-muted mb-0">Order verification, tracking, payments, refunds, and delivery analytics.</p>
        </Col>
        <Col md="6" className="text-md-end mt-3 mt-md-0">
          <Button variant="light" size="sm" className="me-2 border" onClick={fetchData} disabled={loading}>
            <BiRefresh size={20} className={loading ? "spin" : ""} />
          </Button>
          <Form.Select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            style={{ width: 180, display: "inline-block" }}
          >
            {timePeriods.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {loading && (
        <div className="text-center mt-4">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPI Stat Cards */}
          <Row className="g-3 mt-1 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Total Verifications"
                count={formatNumber(data.verification.summary.total)}
                icon={<BiPhoneCall />}
                color={ECHART_COLORS.primary}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Verified"
                count={formatNumber(data.verification.summary.verified)}
                icon={<BiCheckCircle />}
                color={ECHART_COLORS.success}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Escalated to Manual"
                count={formatNumber(data.verification.summary.escalated)}
                icon={<BiTrendingUp />}
                color={ECHART_COLORS.accent}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Avg Attempts / Order"
                count={data.verification.summary.avg_attempts.toString()}
                icon={<BiTime />}
                color={ECHART_COLORS.purple}
              />
            </Col>
          </Row>

          {/* Secondary KPIs */}
          <Row className="g-3 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Failed Verifications"
                count={formatNumber(data.verification.summary.failed)}
                icon={<BiXCircle />}
                color={ECHART_COLORS.danger}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Pending Deliveries"
                count={formatNumber(data.tracking.summary.find((t) => t.status === "Pending")?.count || 0)}
                icon={<BiTime />}
                color={ECHART_COLORS.accentLight}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Refunds Under Process"
                count={formatNumber(data.refunds.summary.total || 0)}
                icon={<BiArrowBack />}
                color={ECHART_COLORS.rose}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Refund Amount"
                count={`Rs ${formatNumber(data.refunds.summary.total_amount || 0)}`}
                icon={<BiDollar />}
                color={ECHART_COLORS.emerald}
              />
            </Col>
          </Row>

          {/* Verification Charts Row */}
          <Row className="g-3 mb-4">
            <Col md="12" lg="4" sm="12" xl="4">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Verification Success Rate</h6>
                <div style={{ height: 280, overflow: "hidden" }}>
                  <EChartsGauge value={verificationRate} title="Verified" />
                </div>
              </Card>
            </Col>
            <Col md="12" lg="4" sm="12" xl="4">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Verification Status Breakdown</h6>
                <div style={{ height: 280, overflow: "hidden" }}>
                  <EChartsDoughnut
                    data={verificationDoughnutData}
                    colors={[ECHART_COLORS.success, ECHART_COLORS.danger, ECHART_COLORS.rose, ECHART_COLORS.accent]}
                  />
                </div>
              </Card>
            </Col>
            <Col md="12" lg="4" sm="12" xl="4">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Order Tracking Status</h6>
                <div style={{ height: 280, overflow: "hidden" }}>
                  <EChartsDoughnut
                    data={trackingDoughnutData}
                    colors={[ECHART_COLORS.accentLight, ECHART_COLORS.teal, ECHART_COLORS.purple, ECHART_COLORS.success, ECHART_COLORS.danger, ECHART_COLORS.rose]}
                  />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Monthly Trend + Payment Methods */}
          <Row className="g-3 mb-4">
            <Col md="12" lg="7" sm="12" xl="7">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Verification Monthly Trend</h6>
                {Object.keys(verifyMonthlyBarData).length > 0 && (
                  <div style={{ height: 300, overflow: "hidden" }}>
                    <EChartsVerticalBar data={verifyMonthlyBarData} label="Verifications" />
                  </div>
                )}
              </Card>
            </Col>
            <Col md="12" lg="5" sm="12" xl="5">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Payment Methods</h6>
                <div style={{ height: 300, overflow: "hidden" }}>
                  <EChartsDoughnut
                    data={paymentMethodDoughnutData}
                    colors={[ECHART_COLORS.primary, ECHART_COLORS.teal, ECHART_COLORS.purple, ECHART_COLORS.accentLight]}
                  />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Delivery Cities */}
          <Row className="g-3 mb-4">
            <Col md="12">
              <Card className="border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">
                  <BiMap className="me-1" /> Top Delivery Cities
                </h6>
                {deliveryCityBarData.length > 0 && (
                  <div style={{ height: 300, overflow: "hidden" }}>
                    <EChartsHorizontalBar
                      data={deliveryCityBarData}
                      label="Deliveries"
                      color={ECHART_COLORS.teal}
                    />
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Recent Verifications + Refunds Tables */}
          <Row className="g-3 mb-4">
            <Col md="12" lg="7" sm="12" xl="7">
              <Card className="border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Recent Order Verifications</h6>
                <div className="table-responsive">
                  <Table hover size="sm" className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-center" style={{ width: 40 }}>#</th>
                        <th>Order Number</th>
                        <th>Status</th>
                        <th className="text-end">Attempts</th>
                        <th>Escalated</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.verification.recentVerifications || []).map((row, i) => (
                        <tr key={row.id}>
                          <td className="text-center text-muted">{i + 1}</td>
                          <td className="fw-medium">{row.order_number}</td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                backgroundColor:
                                  row.status === "VERIFIED" ? ECHART_COLORS.success + "20" :
                                  row.status === "FAILED" ? ECHART_COLORS.danger + "20" :
                                  row.status === "ESCALATED" ? ECHART_COLORS.accent + "20" :
                                  ECHART_COLORS.rose + "20",
                                color:
                                  row.status === "VERIFIED" ? ECHART_COLORS.success :
                                  row.status === "FAILED" ? ECHART_COLORS.danger :
                                  row.status === "ESCALATED" ? ECHART_COLORS.accent :
                                  ECHART_COLORS.rose,
                                fontSize: "0.75rem",
                              }}
                            >
                              {row.status}
                            </span>
                          </td>
                          <td className="text-end">{row.attempt_count}</td>
                          <td>
                            {Number(row.escalated_to_manual) ? (
                              <span className="text-warning fw-bold">Yes</span>
                            ) : (
                              <span className="text-muted">No</span>
                            )}
                          </td>
                          <td className="text-muted" style={{ fontSize: "0.85rem" }}>
                            {row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))}
                      {(!data.verification.recentVerifications || data.verification.recentVerifications.length === 0) && (
                        <tr>
                          <td colSpan="6" className="text-center text-muted py-3">
                            No verification records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
            <Col md="12" lg="5" sm="12" xl="5">
              <Card className="border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Recent Refunds</h6>
                <div className="table-responsive">
                  <Table hover size="sm" className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-center" style={{ width: 40 }}>#</th>
                        <th>Sub. ID</th>
                        <th>Status</th>
                        <th className="text-end">Amount</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.refunds.recent || []).map((row, i) => (
                        <tr key={row.refund_id}>
                          <td className="text-center text-muted">{i + 1}</td>
                          <td>{row.subscription_id}</td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                backgroundColor: ECHART_COLORS.rose + "20",
                                color: ECHART_COLORS.rose,
                                fontSize: "0.75rem",
                              }}
                            >
                              {row.refund_status}
                            </span>
                          </td>
                          <td className="text-end fw-bold">Rs {formatNumber(row.refund_amount)}</td>
                          <td className="text-muted" style={{ fontSize: "0.85rem" }}>
                            {row.refund_date ? new Date(row.refund_date).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))}
                      {(!data.refunds.recent || data.refunds.recent.length === 0) && (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-3">
                            No refund records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default OrderOperationsDashboard;
